/**
 * What the visitor has agreed to, purpose by purpose.
 *
 * The site puts three kinds of non-essential thing on a device, and they are
 * genuinely different questions rather than one question in three costumes:
 *
 *   analytics  counting which pages get read, in aggregate
 *   behaviour  recording how an individual visit moves, scrolls and clicks
 *   marketing  telling an advertising network that this person was here
 *
 * Somebody may reasonably be happy to be counted, uneasy about being recorded,
 * and unwilling to be advertised to — and under Article 4(11) and Article 6(1)(a)
 * of the GDPR consent has to be specific to each purpose, so those three answers
 * have to be askable separately. One switch covering all three would not be
 * consent to any of them.
 *
 * Everything else the site stores — the language choice, the theme, the
 * signed-in session on the dashboard — is either required to deliver something
 * the visitor asked for or is the visitor's own setting, and under Article 5(3)
 * of the ePrivacy Directive (in Lithuania, Article 99 of the Elektroninių ryšių
 * įstatymas) those need no consent and are not asked about.
 *
 * The consequence worth stating plainly: refusing all three costs the visitor
 * nothing. The language switcher, the booking form and the dashboard work
 * exactly the same either way, which is what makes an honest decline possible.
 *
 * Nothing here is a cookie. The record lives in localStorage, is never sent to
 * a server, and is itself strictly necessary — remembering that someone said no
 * is the only way to avoid asking them again on every page.
 */

export const CONSENT_KEY = 'noventi_cookie_consent';

/**
 * Bumped from 1 to 2 when behaviour and marketing were added.
 *
 * This is the mechanism that stops a new purpose from riding in on an old
 * answer. Everyone who agreed when the only question was analytics is asked
 * again, because they were never asked about session recording or about being
 * reported to an advertising network, and treating their old yes as covering
 * either would be the ordinary way a consent banner becomes a lie.
 *
 * Bump it again for the next purpose. Never reuse an existing key for a purpose
 * it was not named for.
 */
export const CONSENT_VERSION = 2;

/** Fired on `window` when the answer changes, so the tags and the banner do not
 *  need a shared provider to stay in step. */
export const CONSENT_EVENT = 'noventi:consent';

/** Fired on `window` to ask the banner to come back, which is how withdrawing
 *  consent is made exactly as easy as giving it. */
export const CONSENT_REOPEN_EVENT = 'noventi:consent-reopen';

export const PURPOSES = ['analytics', 'behaviour', 'marketing'] as const;
export type Purpose = (typeof PURPOSES)[number];

/** What each purpose is for, in the words the banner uses. The description is
 *  what a visitor reads before deciding, so it says what actually happens
 *  rather than what it is called. */
export const PURPOSE_COPY: Record<Purpose, { label: string; description: string }> = {
  analytics: {
    label: 'Analyse site usage',
    description:
      'Counts which pages are read and where people leave, so we can tell what is worth keeping.',
  },
  behaviour: {
    label: 'Improve site navigation',
    description:
      'Records how visits move through the pages — scrolling, clicks, where people get stuck — so confusing layouts can be found and fixed.',
  },
  marketing: {
    label: 'Improve marketing',
    description:
      'Tells Meta that you visited, so our advertising can be measured and shown to people like you. This one shares data with a company outside our control.',
  },
};

export type PurposeAnswers = Record<Purpose, boolean>;

export interface ConsentChoice extends PurposeAnswers {
  version: number;
  /** When the answer was given. Kept because a controller has to be able to
   *  show that consent was obtained, and when. */
  at: string;
}

type ConsentStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** All three refused — the state everything starts in, and the state an
 *  unreadable or outdated record falls back to. */
export function noConsent(): PurposeAnswers {
  return { analytics: false, behaviour: false, marketing: false };
}

/**
 * The stored answer, or null when there is none to read — which is the state
 * that shows the banner. An answer to an older version of the question is no
 * answer at all, and neither is anything malformed.
 */
export function readConsent(storage: ConsentStorage | undefined): ConsentChoice | null {
  if (!storage) return null;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(CONSENT_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    const stored = parsed as Record<string, unknown>;
    if (stored.version !== CONSENT_VERSION) return null;
    /* Every purpose must be present and boolean. A record missing one is a
       record from a shape we no longer understand, and guessing the missing
       answer is exactly the guess that must not be made in this direction. */
    const answers = noConsent();
    for (const purpose of PURPOSES) {
      if (typeof stored[purpose] !== 'boolean') return null;
      answers[purpose] = stored[purpose] as boolean;
    }
    return {
      ...answers,
      version: CONSENT_VERSION,
      at: typeof stored.at === 'string' ? stored.at : '',
    };
  } catch {
    /* A browser with storage blocked answers by throwing. Treat that as "not
       asked yet": no consent, nothing loaded, banner shown. */
    return null;
  }
}

export function writeConsent(
  storage: ConsentStorage | undefined,
  answers: PurposeAnswers,
  now = new Date()
): ConsentChoice {
  const choice: ConsentChoice = {
    ...noConsent(),
    ...answers,
    version: CONSENT_VERSION,
    at: now.toISOString(),
  };
  try {
    storage?.setItem(CONSENT_KEY, JSON.stringify(choice));
  } catch {
    /* Private browsing with storage disabled. The choice still governs this
       page view; it simply cannot be remembered for the next one. */
  }
  return choice;
}

/** Whether anything at all was agreed to. */
export function anyGranted(answers: PurposeAnswers): boolean {
  return PURPOSES.some((purpose) => answers[purpose]);
}

export type ConsentSignal = 'granted' | 'denied';

/**
 * The answers expressed as Google Consent Mode v2 signals.
 *
 * The advertising signals now follow the marketing answer rather than being
 * denied unconditionally, because marketing is a thing this site does once a
 * Meta pixel is configured. They are still denied for everyone who did not
 * agree to it, and denied for everyone at all times when no pixel is
 * configured — the caller decides that by never granting a purpose it does not
 * ask about.
 *
 * `security_storage` and `functionality_storage` are granted because that is
 * what strictly necessary means — they cover the session and the language,
 * which are not in question.
 */
export function consentSignals(answers: PurposeAnswers): Record<string, ConsentSignal> {
  const signal = (granted: boolean): ConsentSignal => (granted ? 'granted' : 'denied');
  return {
    ad_storage: signal(answers.marketing),
    ad_user_data: signal(answers.marketing),
    ad_personalization: signal(answers.marketing),
    analytics_storage: signal(answers.analytics),
    functionality_storage: 'granted',
    personalization_storage: signal(answers.marketing),
    security_storage: 'granted',
  };
}
